import { AccountEntity } from '@shared/api/accountConfig/accountConfig.interface';
import { GiftRebateSettlement } from '@shared/api/giftRebateSettlement/giftRebateSettlement.interface';
import { GiftSupplySettlement } from '@shared/api/giftSupplySettlement/giftSupplySettlement.interface';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';

import { rebateMariShare, sumMariShare } from './mari-share.utils';
import { settlementDisplayTotal } from './settlement.utils';
import { statusLabel } from './rebate-settlement.utils';

// ─────────────────────────────────────────────
// 정산 완료 내역(목록) 요약 엑셀 다운로드
//
// 배송업체/상조 정산 페이지(관리자·GiftAgent)의 업체정산/상조정산 완료 내역 표를
// 화면에 보이는 그대로(필터 적용 후) 정산 1건당 1행으로 내보낸다.
// 월별 라인 상세는 각 행의 "상세보기" 모달 엑셀에서 받는다.
//
// 프로젝트 표준(커뮤니티 xlsx + file-saver)을 따른다. 셀 색/굵기/테두리 스타일은
// 미지원이라, 가시성은 라이브러리가 지원하는 범위 — 열 너비·행 높이, 숫자 천단위
// 포맷, 헤더 고정, 자동 필터 — 로 확보한다.
// ─────────────────────────────────────────────

const NUM_FMT = '#,##0';
const MONEY_LABELS = ['정산금액', '상조 정산금①', '마리 수익금 1%②', '계산서 총액①+②'];
const HEADER_ROW_PT = 28;
const BODY_ROW_PT = 22;

const fmtD = (d?: Date | string): string => {
  if (!d) return '';
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate(),
  ).padStart(2, '0')}`;
};

/** 컬럼 폭 자동 조정 (한글 2.2칸/영문 1칸 기준, 숫자는 콤마 폭 보정). */
function autoWidth(ws: XLSX.WorkSheet) {
  const range = XLSX.utils.decode_range(ws['!ref'] ?? 'A1');
  const cols: XLSX.ColInfo[] = [];
  for (let C = range.s.c; C <= range.e.c; C++) {
    let max = 8;
    for (let R = range.s.r; R <= range.e.r; R++) {
      const cell = ws[XLSX.utils.encode_cell({ r: R, c: C })];
      if (cell?.v == null) continue;
      let text = String(cell.v);
      if (typeof cell.v === 'number') {
        const digits = Math.abs(Math.trunc(cell.v)).toString().length;
        text += ','.repeat(Math.max(0, Math.floor((digits - 1) / 3)));
      }
      let w = 0;
      for (const ch of text) w += ch.charCodeAt(0) > 0x7f ? 2.2 : 1;
      if (w > max) max = w;
    }
    cols.push({ wch: Math.min(Math.ceil(max) + 4, 60) });
  }
  ws['!cols'] = cols;
}

/** 헤더는 높게, 데이터 행은 넉넉히 — 글씨가 답답해 보이지 않도록 행 높이를 키운다. */
function setRowHeights(ws: XLSX.WorkSheet) {
  const range = XLSX.utils.decode_range(ws['!ref'] ?? 'A1');
  const rows: XLSX.RowInfo[] = [];
  for (let R = range.s.r; R <= range.e.r; R++) {
    rows[R] = { hpt: R === 0 ? HEADER_ROW_PT : BODY_ROW_PT };
  }
  ws['!rows'] = rows;
}

/** 헤더 라벨이 금액 컬럼이면 #,##0 숫자 포맷 적용 */
function applyMoneyFormat(ws: XLSX.WorkSheet) {
  const range = XLSX.utils.decode_range(ws['!ref'] ?? 'A1');
  const moneyCols: number[] = [];
  for (let C = range.s.c; C <= range.e.c; C++) {
    const hdr = ws[XLSX.utils.encode_cell({ r: 0, c: C })];
    if (hdr && MONEY_LABELS.includes(String(hdr.v))) moneyCols.push(C);
  }
  for (let R = range.s.r + 1; R <= range.e.r; R++) {
    for (const C of moneyCols) {
      const cell = ws[XLSX.utils.encode_cell({ r: R, c: C })];
      if (cell && typeof cell.v === 'number') cell.z = NUM_FMT;
    }
  }
}

/** 헤더 행(1행) 고정 */
function freezeHeader(ws: XLSX.WorkSheet) {
  ws['!freeze'] = { xSplit: 0, ySplit: 1 };
  if (!ws['!views']) ws['!views'] = [];
  (ws['!views'] as Array<Record<string, unknown>>)[0] = { state: 'frozen', ySplit: 1 };
}

/** 헤더 행에 자동 필터. 합계 행은 정렬/필터에 휩쓸리지 않게 범위에서 제외. */
function applyAutoFilter(ws: XLSX.WorkSheet) {
  const range = XLSX.utils.decode_range(ws['!ref'] ?? 'A1');
  if (range.e.r > range.s.r) range.e.r -= 1;
  ws['!autofilter'] = { ref: XLSX.utils.encode_range(range) };
}

/** 폭/높이/숫자포맷/헤더고정/자동필터를 한 번에 적용해 시트를 마무리한다. */
function finishSheet(ws: XLSX.WorkSheet) {
  autoWidth(ws);
  setRowHeights(ws);
  applyMoneyFormat(ws);
  freezeHeader(ws);
  applyAutoFilter(ws);
}

function writeBook(rows: Record<string, unknown>[], sheetName: string, fileName: string) {
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.json_to_sheet(rows);
  finishSheet(ws);
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  saveAs(new Blob([buf], { type: 'application/octet-stream' }), fileName);
}

const supplyEntityLabel = (entity?: AccountEntity): string =>
  entity === AccountEntity.Mari ? '마리' : entity === AccountEntity.Gaon ? '가온' : '';

/**
 * 업체정산 완료 내역(목록)을 요약 엑셀로 다운로드한다.
 * @param items 화면 필터 적용 후의 정산 문서 목록
 * @param giftAgentView GiftAgent(마리) 뷰면 '정산 주체' 컬럼을 숨긴다(전부 마리).
 */
export function exportSupplyHistorySummaryExcel(
  items: GiftSupplySettlement[],
  giftAgentView = false,
) {
  const rows: Record<string, unknown>[] = items.map((s) => {
    const brand = s.giftItemBrand;
    const brandName = typeof brand === 'object' ? brand.name : brand || '-';
    const entity =
      typeof brand === 'object' ? brand.settlementInfo?.accountEntity : undefined;
    return {
      업체: brandName,
      ...(giftAgentView ? {} : { '정산 주체': supplyEntityLabel(entity) }),
      정산월: `${s.year}년 ${s.month}월`,
      상태: statusLabel(s.status),
      건수: s.lines.length,
      정산금액: settlementDisplayTotal(s),
      지급일: fmtD(s.executedAt),
      메모: s.memo ?? '',
    };
  });

  rows.push({
    업체: '■ 합계',
    ...(giftAgentView ? {} : { '정산 주체': '' }),
    정산월: '',
    상태: '',
    건수: items.reduce((sum, s) => sum + s.lines.length, 0),
    정산금액: items.reduce((sum, s) => sum + settlementDisplayTotal(s), 0),
    지급일: '',
    메모: '',
  });

  writeBook(rows, '업체정산완료', '업체정산완료내역.xlsx');
}

/**
 * 상조정산 완료 내역(목록)을 요약 엑셀로 다운로드한다.
 * @param items 화면 필터 적용 후의 정산 문서 목록
 * @param giftAgentView GiftAgent(마리) 뷰면 '경유' 컬럼을 숨긴다(전부 마리 경유).
 * @param options.withMariShare 마리 수익금(판매가 1%) 컬럼 포함 — **admin·마리 전용**.
 *   상조에게 나가는 파일에는 절대 켜지 않는다.
 */
export function exportRebateHistorySummaryExcel(
  items: GiftRebateSettlement[],
  giftAgentView = false,
  options: { withMariShare?: boolean } = {},
) {
  const { withMariShare = false } = options;
  const rows: Record<string, unknown>[] = items.map((s) => {
    const bb = s.bugoBrand;
    const bbName =
      typeof bb === 'object'
        ? bb.bugoAgency?.corpName || bb.bugoAgency?.nickName || bb._id
        : bb || '-';
    const viaMari =
      typeof bb === 'object' ? bb.giftAccountConfig?.viaMari : undefined;
    const total = settlementDisplayTotal(s);
    // 마리 분배분은 상조 정산금과 별개 — 정산금액에 섞지 않고 옆 컬럼으로 둔다
    const mariShare = withMariShare ? rebateMariShare(s) : null;
    return {
      상조: bbName,
      ...(giftAgentView
        ? {}
        : { 경유: viaMari === undefined ? '' : viaMari ? '마리 경유' : '가온 직접' }),
      정산월: `${s.year}년 ${s.month}월`,
      상태: statusLabel(s.status),
      건수: s.lines.length,
      ...(withMariShare
        ? {
            '상조 정산금①': total,
            '마리 수익금 1%②': mariShare ?? '',
            '계산서 총액①+②': total + (mariShare ?? 0),
            세금구분: '과세(부가세 포함)',
          }
        : { 정산금액: total }),
      지급일: fmtD(s.executedAt),
      메모: s.memo ?? '',
    };
  });

  const totalAmount = items.reduce((sum, s) => sum + settlementDisplayTotal(s), 0);
  const totalMariShare = withMariShare
    ? sumMariShare(items.map((s) => rebateMariShare(s)))
    : 0;

  rows.push({
    상조: '■ 합계',
    ...(giftAgentView ? {} : { 경유: '' }),
    정산월: '',
    상태: '',
    건수: items.reduce((sum, s) => sum + s.lines.length, 0),
    ...(withMariShare
      ? {
          '상조 정산금①': totalAmount,
          '마리 수익금 1%②': totalMariShare,
          '계산서 총액①+②': totalAmount + totalMariShare,
          세금구분: '',
        }
      : { 정산금액: totalAmount }),
    지급일: '',
    메모: '',
  });

  writeBook(rows, '상조정산완료', '상조정산완료내역.xlsx');
}
